import React from "react";
import { Chip, Progress, Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { usePlaygroundStore, type NodeStatus } from "@/stores/PlaygroundStore";
import { useTheme } from "@/hooks/use-theme";

const stepStatusConfig: Record<
  NodeStatus,
  { icon: string; colour: string; chipColour: "default" | "warning" | "success" | "danger" }
> = {
  idle: {
    icon: "solar:clock-circle-linear",
    colour: "text-default-400",
    chipColour: "default",
  },
  running: {
    icon: "solar:refresh-circle-linear",
    colour: "text-warning",
    chipColour: "warning",
  },
  success: {
    icon: "solar:check-circle-linear",
    colour: "text-success",
    chipColour: "success",
  },
  error: {
    icon: "solar:close-circle-linear",
    colour: "text-danger",
    chipColour: "danger",
  },
};

const PlaygroundProgressTimeline: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { isRunning, workflowProgress, nodeStatuses } = usePlaygroundStore();

  // Only show when running or when there is progress data
  if (!isRunning && workflowProgress.totalSteps === 0) {
    return null;
  }

  const { totalSteps, completedSteps, currentStep, executionOrder } = workflowProgress;
  const percentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-2 border-b transition-all duration-300
        ${isDark ? "bg-default-50 border-default-200" : "bg-gray-50/80 border-default-200"}
      `}
    >
      {/* Progress indicator */}
      <div className="flex items-center gap-2 shrink-0">
        {isRunning ? (
          <Icon className="w-4 h-4 text-primary animate-spin" icon="solar:refresh-circle-linear" />
        ) : percentage === 100 ? (
          <Icon className="w-4 h-4 text-success" icon="solar:check-circle-bold" />
        ) : (
          <Icon className="w-4 h-4 text-default-400" icon="solar:play-circle-linear" />
        )}
        <span className="text-xs font-medium text-default-600">
          {isRunning ? t("Running") : percentage === 100 ? t("Complete") : t("Stopped")}
        </span>
      </div>

      {/* Progress bar */}
      <Progress
        className="max-w-[200px]"
        color={isRunning ? "primary" : percentage === 100 ? "success" : "warning"}
        isIndeterminate={isRunning && completedSteps === 0}
        size="sm"
        value={percentage}
      />

      <span className="text-tiny text-default-400 shrink-0">
        {completedSteps}/{totalSteps}
      </span>

      {/* Step badges */}
      <div className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0">
        {executionOrder.map((stepId) => {
          const status = nodeStatuses[stepId] || "idle";
          const config = stepStatusConfig[status];
          const isCurrent = stepId === currentStep;

          return (
            <Tooltip key={stepId} content={`${stepId}: ${status}`}>
              <Chip
                className={`text-tiny shrink-0 transition-all duration-200 ${
                  isCurrent ? "ring-2 ring-primary-400 ring-offset-1 scale-105" : ""
                }`}
                color={config.chipColour}
                size="sm"
                startContent={
                  <Icon
                    className={`w-3 h-3 ${status === "running" ? "animate-spin" : ""}`}
                    icon={config.icon}
                  />
                }
                variant={isCurrent ? "solid" : "flat"}
              >
                {stepId}
              </Chip>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default PlaygroundProgressTimeline;
